import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Alert,
} from '@mui/material';

interface ApplyLevelUpModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (stat: string) => Promise<void>;
}

const STAT_OPTIONS = [
  { value: 'F', label: 'Fight' },
  { value: 'S', label: 'Shoot' },
  { value: 'W', label: 'Will' },
  { value: 'H', label: 'Health' },
];

export const ApplyLevelUpModal: React.FC<ApplyLevelUpModalProps> = ({ open, onClose, onApply }) => {
  const [selected, setSelected] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setSelected(null);
      setError(null);
    }
  }, [open]);

  const handleApply = async () => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      await onApply(selected);
      onClose();
    } catch {
      setError('Failed to apply level up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Apply Level Up</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Choose a stat to increase.
        </Typography>
        <ToggleButtonGroup
          value={selected}
          exclusive
          onChange={(_, value) => { if (value !== null) setSelected(value); }}
          fullWidth
        >
          {STAT_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value} disabled={loading}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={loading || !selected}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
